import { closestCenter, DndContext, type DragEndEvent, KeyboardSensor, PointerSensor, useSensor, useSensors } from "@dnd-kit/core";
import { restrictToParentElement, restrictToVerticalAxis } from "@dnd-kit/modifiers";
import { arrayMove, SortableContext, sortableKeyboardCoordinates, useSortable, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { Table as MantineTable, Popover, rgba } from "@mantine/core";
import { IconColumns, IconGripVertical, IconLock, IconPencil, IconRefresh, type TablerIcon } from "@tabler/icons-react";
import { humanize, DataTable as MantineDataTable } from "mantine-datatable";
import * as React from "react";

import { ActionIcon } from "../controls/buttons";
import { Checkbox } from "../controls/toggles";
import toggleClasses from "../controls/toggles.module.css";
import { UnstyledButton } from "../primitives/buttons";
import { Flex } from "../primitives/layout";
import { rem } from "../primitives/style-props";
import { Divider, ScrollArea } from "../primitives/surfaces";
import { Text } from "../primitives/typography";
import { mantineColorRamps, tokens } from "../tokens";

import { Tooltip } from "./feedback";
import { useDisclosure, useLocalStorage } from "./hooks";
import { defaultDataTableColumnState, effectiveDataTableColumns, reconcileDataTableColumnState } from "./reconcileDataTableColumnState";
import css from "./tables.module.css";

export type DataTableSortDirection = "asc" | "desc";

export interface DataTableSortStatus {
  columnAccessor: string;
  direction: DataTableSortDirection;
}

export interface DataTableColumn<T> {
  accessor: string;
  title?: React.ReactNode;
  render?: (record: T, index: number) => React.ReactNode;
  textAlignment?: "left" | "center" | "right";
  sortable?: boolean;
  draggable?: boolean;
  toggleable?: boolean;
  resizable?: boolean;
  defaultToggle?: boolean;
  filter?: React.ReactNode | ((props: { close: () => void }) => React.ReactNode);
  filtering?: boolean;
  width?: string | number;
  hidden?: boolean;
  visibleMediaQuery?: string;
  titleClassName?: string;
  titleStyle?: React.CSSProperties;
  cellsClassName?: string | ((record: T, recordIndex: number) => string | undefined);
  cellsStyle?: React.CSSProperties | ((record: T, recordIndex: number) => React.CSSProperties | undefined);
  customCellAttributes?: (record: T, recordIndex: number) => Record<string, unknown>;
  footer?: React.ReactNode;
  footerClassName?: string;
  footerStyle?: React.CSSProperties;
  ellipsis?: boolean;
  noWrap?: boolean;
}

/** The owned column shape used by the opinionated table wrapper. */
export interface DatatableColumn<T> extends DataTableColumn<T> {
  /** Preferred application spelling; normalised to textAlignment at the private vendor edge. */
  alignment?: "left" | "center" | "right";
}

export interface DataTableColumnToggle {
  accessor: string;
  defaultToggle: boolean;
  toggleable: boolean;
  toggled: boolean;
}

export interface DataTableColumnState {
  columnsOrder: string[];
  columnsToggle: DataTableColumnToggle[];
}

export interface DataTableColumnsState<T> extends DataTableColumnState {
  effectiveColumns: DataTableColumn<T>[];
  setColumnsOrder: React.Dispatch<React.SetStateAction<string[]>>;
  resetColumnsOrder: () => void;
  setColumnsToggle: React.Dispatch<React.SetStateAction<DataTableColumnToggle[]>>;
  resetColumnsToggle: () => void;
  columnsWidth: Array<Record<string, string | number>>;
  setColumnsWidth: React.Dispatch<React.SetStateAction<Array<Record<string, string | number>>>>;
  setColumnWidth: (accessor: string, width: string | number) => void;
  resetColumnsWidth: () => void;
}

interface DataTableRowExpansionProps<T> {
  trigger?: "click" | "always" | "never";
  allowMultiple?: boolean;
  expanded?: {
    recordIds: string[];
    onRecordIdsChange?: (recordIds: string[]) => void;
  };
  content: (props: { record: T; recordIndex: number; collapse: () => void }) => React.ReactNode;
}

interface DataTableCellClickParams<T> {
  event: React.MouseEvent;
  record: T;
  recordIndex: number;
  column: DataTableColumn<T>;
  columnIndex: number;
}

export interface DataTableProps<T> {
  columns: DataTableColumn<T>[];
  records?: T[];
  className?: string;
  classNames?: Record<string, string | undefined>;
  style?: React.CSSProperties;
  height?: string | number;
  minHeight?: string | number;
  mih?: string | number;
  w?: string | number;
  m?: string | number;
  mt?: string | number;
  shadow?: string;
  withBorder?: boolean;
  withColumnBorders?: boolean;
  borderColor?: string;
  rowBorderColor?: string;
  textSelectionDisabled?: boolean;
  verticalAlignment?: "top" | "center" | "bottom";
  fetching?: boolean;
  pinLastColumn?: boolean;
  defaultColumnProps?: Partial<DataTableColumn<T>>;
  storeColumnsKey?: string;
  defaultColumnRender?: (record: T, index: number, accessor: string) => React.ReactNode;
  idAccessor?: string | ((record: T) => React.Key);
  noRecordsText?: string;
  noRecordsIcon?: React.ReactNode | React.ElementType;
  emptyState?: React.ReactNode;
  noHeader?: boolean;
  onCellClick?: (params: DataTableCellClickParams<T>) => void;
  onRowClick?: (record: T, recordIndex: number, event: React.MouseEvent) => void;
  onScrollToTop?: () => void;
  onScrollToBottom?: () => void;
  onScrollToLeft?: () => void;
  onScrollToRight?: () => void;
  rowContextMenu?: unknown;
  rowExpansion?: DataTableRowExpansionProps<T>;
  rowClassName?: string | ((record: T, recordIndex: number) => string | undefined);
  rowStyle?: React.CSSProperties | ((record: T, recordIndex: number) => React.CSSProperties | undefined);
  customRowAttributes?: (record: T, recordIndex: number) => Record<string, unknown>;
  scrollViewportRef?: React.RefObject<HTMLDivElement>;
  scrollAreaProps?: Record<string, unknown>;
  bodyRef?: React.RefObject<HTMLTableSectionElement> | ((node: HTMLTableSectionElement | null) => void);
  striped?: boolean | "odd" | "even";
  highlightOnHover?: boolean;
  /** Keeps the final cell visible only while a row is hovered. Opt-in for action-heavy direct tables. */
  hoverStickyLastColumn?: boolean;
  horizontalSpacing?: string | number;
  verticalSpacing?: string | number;
  fontSize?: string | number;
  paginationText?: (props: { from: number; to: number; totalRecords: number }) => React.ReactNode;
  page?: number;
  onPageChange?: (page: number) => void;
  totalRecords?: number;
  recordsPerPage?: number;
  sortStatus?: DataTableSortStatus;
  onSortStatusChange?: (status: DataTableSortStatus) => void;
  selectedRecords?: T[];
  onSelectedRecordsChange?: (records: T[]) => void;
  isRecordSelectable?: (record: T, recordIndex: number) => boolean;
  /** Replaces the vendor's default row checkbox props (its "Select record N" aria-label included). */
  getRecordSelectionCheckboxProps?: (record: T, recordIndex: number) => { "aria-label": string; wrapperProps?: { title: string } };
  /** Owned label for the header select-all checkbox. */
  selectionHeaderLabel?: string;
  /** Disables only the native header select-all, leaving row selection to isRecordSelectable. */
  selectionHeaderDisabled?: boolean;
}

export interface TableProps extends React.TableHTMLAttributes<HTMLTableElement> {
  striped?: boolean | "odd" | "even";
  highlightOnHover?: boolean;
  withBorder?: boolean;
  withColumnBorders?: boolean;
  horizontalSpacing?: string | number;
  verticalSpacing?: string | number;
  fontSize?: string | number;
  m?: string | number;
  mt?: string | number;
}

/**
 * A column as mantine-datatable 7 reads it (SC-2703): textAlignment is renamed textAlign and cellsStyle is
 * accepted only as a function. The owned DataTableColumn spelling is unchanged.
 */
type VendorDataTableColumn<T> = Omit<DataTableColumn<T>, "textAlignment" | "cellsStyle"> & {
  textAlign?: DataTableColumn<T>["textAlignment"];
  cellsStyle?: (record: T, index: number) => React.CSSProperties | undefined;
};

/** Owned DataTable props the facade renames or reshapes; tables-casts.test.ts pins every other key as a v7 prop. */
type DataTableAdaptedProps =
  | "columns"
  | "defaultColumnProps"
  | "hoverStickyLastColumn"
  | "withBorder"
  | "verticalAlignment"
  | "fontSize"
  | "rowContextMenu"
  | "onCellClick"
  | "onRowClick"
  | "onSortStatusChange"
  | "rowExpansion"
  | "rowStyle"
  | "selectionHeaderLabel"
  | "selectionHeaderDisabled";

/**
 * The props the facade hands mantine-datatable 7. Mantine 7 renamed withBorder to withTableBorder and
 * verticalAlignment to verticalAlign, dropped fontSize for the fz style prop, passes `index` where the
 * owned callbacks name `recordIndex`, and takes row and cell styles only as functions.
 */
interface VendorDataTableProps<T> extends Omit<DataTableProps<T>, DataTableAdaptedProps> {
  columns: VendorDataTableColumn<T>[];
  defaultColumnProps?: Partial<VendorDataTableColumn<T>>;
  withTableBorder?: boolean;
  withRowBorders: boolean;
  verticalAlign?: DataTableProps<T>["verticalAlignment"];
  fz?: DataTableProps<T>["fontSize"];
  onCellClick?: (params: { event: React.MouseEvent; record: T; index: number; columnIndex: number }) => void;
  onRowClick?: (params: { event: React.MouseEvent; record: T; index: number }) => void;
  onSortStatusChange?: (status: DataTableSortStatus) => void;
  rowExpansion?: Omit<DataTableRowExpansionProps<T>, "content"> & {
    content: (params: { record: T; index: number; collapse: () => void }) => React.ReactNode;
  };
  rowStyle?: (record: T, index: number) => React.CSSProperties | undefined;
  allRecordsSelectionCheckboxProps: HeaderSelectionCheckboxProps;
}

/** The props the facade gives mantine-datatable 7's header select-all Checkbox (see ALL_RECORDS_SELECTION_CHECKBOX). */
interface HeaderSelectionCheckboxProps {
  "aria-label": string;
  disabled?: boolean;
  classNames: { input?: string; icon?: string };
}

/** Owned Table props that Table applies itself with Mantine 6's rules instead of forwarding to Mantine 7. */
type TableStylingProps =
  "striped" | "highlightOnHover" | "withBorder" | "withColumnBorders" | "horizontalSpacing" | "verticalSpacing" | "fontSize";

/** Owned Table props with Mantine 7's withTableBorder; tables-casts.test.ts pins every forwarded key. */
interface TableAdapterProps extends Omit<TableProps, TableStylingProps> {
  withTableBorder?: boolean;
}

/**
 * Mantine types these roots in terms of vendor contracts. This private cast gives them the adapter
 * surfaces above; tables-casts.test.ts pins every forwarded key and renamed target against Mantine 7.
 */
type DataTableRootComponent = <T>(props: VendorDataTableProps<T>) => React.ReactElement | null;
const DataTableRoot = MantineDataTable as DataTableRootComponent;
const TableRoot = MantineTable as React.ComponentType<TableAdapterProps>;

const joinClassNames = (...classNames: Array<string | undefined>) => classNames.filter(Boolean).join(" ");

/** mantine-datatable 6's default row separator: the theme's gray.3 at 65% opacity (v7 draws it opaque). */
const MANTINE_6_ROW_BORDER_COLOR = rgba(mantineColorRamps.gray[3], 0.65);

/**
 * mantine-datatable 7 renders its header select-all box as its own @mantine/core Checkbox, so the owned
 * Checkbox's Mantine 6 indeterminate look (a white box with a text-coloured dash, toggles.module.css) never
 * reached it; Mantine 7 fills a partly-selected header box like a checked one. The vendor spreads these props
 * over its own, replacing its default aria-label and its `classNames`, so both are restated: the label
 * verbatim and the input's pointer-cursor class (mantine-datatable 7.17.1 dist/index.mjs, header selector
 * cell) beside the owned slot classes (SC-2703).
 */
const ALL_RECORDS_SELECTION_CHECKBOX: HeaderSelectionCheckboxProps = {
  "aria-label": "Select all records",
  classNames: {
    input: joinClassNames("mantine-datatable-pointer-cursor", toggleClasses.checkboxInput),
    icon: toggleClasses.checkboxIcon,
  },
};

/** Mantine 7 takes row and cell styles only as functions; an owned style object becomes a constant one. */
function toVendorStyle<T>(
  style: React.CSSProperties | ((record: T, index: number) => React.CSSProperties | undefined) | undefined,
): ((record: T, index: number) => React.CSSProperties | undefined) | undefined {
  if (style === undefined || typeof style === "function") {
    return style;
  }
  const staticStyle: React.CSSProperties = style;
  return () => staticStyle;
}

/**
 * Keys are copied only when present, so a column still inherits defaultColumnProps exactly as it did.
 * Without a vendor storage key, mantine-datatable 7's toggle popover reads undefined column state and
 * throws while rendering, and its resize handle throws on every drag, so toggleable and resizable reach
 * the vendor only with a key.
 */
function toVendorColumnFields<T>(column: Partial<DataTableColumn<T>>, columnStateStored: boolean): Partial<VendorDataTableColumn<T>> {
  const { textAlignment, cellsStyle, toggleable, resizable, ...sharedFields } = column;
  return {
    ...sharedFields,
    ...("textAlignment" in column ? { textAlign: textAlignment } : {}),
    ...("cellsStyle" in column ? { cellsStyle: toVendorStyle(cellsStyle) } : {}),
    ...(columnStateStored && "toggleable" in column ? { toggleable } : {}),
    ...(columnStateStored && "resizable" in column ? { resizable } : {}),
  };
}

/** Mantine 6 reported the clicked definition without its hidden flag; columnIndex indexes the columns handed over. */
function clickedColumn<T>(columns: DataTableColumn<T>[], columnIndex: number): DataTableColumn<T> {
  const column = columns[columnIndex];
  if (column === undefined) {
    throw new Error(`DataTable reported a click on column ${columnIndex} of the ${columns.length} it was given`);
  }
  const { hidden: _hidden, ...definition } = column;
  return definition;
}

/** Mantine 7 names the record position `index` and reports its own column shape; the owned payload is Mantine 6's. */
function toVendorCellClick<T>(
  columns: DataTableColumn<T>[],
  onCellClick: DataTableProps<T>["onCellClick"],
): VendorDataTableProps<T>["onCellClick"] {
  if (onCellClick === undefined) {
    return undefined;
  }
  return ({ event, record, index, columnIndex }) =>
    onCellClick({ event, record, recordIndex: index, column: clickedColumn(columns, columnIndex), columnIndex });
}

function toVendorRowExpansion<T>({ content, ...rowExpansion }: DataTableRowExpansionProps<T>): VendorDataTableProps<T>["rowExpansion"] {
  return {
    ...rowExpansion,
    content: ({ record, index, collapse }) => content({ record, recordIndex: index, collapse }),
  };
}

/**
 * The keyed persistence hook keeps Mantine 6's storage format and keys: `${key}-columns-order`, `-toggle`
 * and `-width`, read by the order-table migrations, through the owned useLocalStorage exactly as Mantine 6's
 * hook called it. It owns that storage because mantine-datatable 7's hook prunes stored accessors that the
 * current definitions lack, and reconciliation is append-only by design (SC-2703).
 */
export function useDatatableColumns<T>({
  key,
  columns,
}: {
  key: string | null | undefined;
  columns: DataTableColumn<T>[];
}): DataTableColumnsState<T> {
  const storageKey = key ?? undefined;
  // Mantine 6 built its keys from the raw key, so a missing key read and wrote the literal "undefined" prefix.
  const keyPrefix = String(storageKey);
  const defaults = React.useMemo(() => defaultDataTableColumnState(columns), [columns]);
  const [columnsOrder, setColumnsOrder] = useLocalStorage({ key: `${keyPrefix}-columns-order`, defaultValue: defaults.columnsOrder });
  const [columnsToggle, setColumnsToggle] = useLocalStorage({ key: `${keyPrefix}-columns-toggle`, defaultValue: defaults.columnsToggle });
  const [columnsWidth, setColumnsWidth] = useLocalStorage({ key: `${keyPrefix}-columns-width`, defaultValue: defaults.columnsWidth });
  const effectiveColumns = React.useMemo(
    () => effectiveDataTableColumns(columns, { columnsOrder, columnsToggle, columnsWidth }),
    [columns, columnsOrder, columnsToggle, columnsWidth],
  );
  const lastStorageKey = React.useRef(storageKey);
  const reconciledState = React.useMemo(() => {
    const state: DataTableColumnState = { columnsOrder, columnsToggle };
    return storageKey === undefined ? state : reconcileDataTableColumnState(columns, state);
  }, [columnsOrder, columnsToggle, columns, storageKey]);

  React.useEffect(() => {
    if (lastStorageKey.current !== storageKey) {
      lastStorageKey.current = storageKey;
      return;
    }
    if (storageKey === undefined) {
      return;
    }
    if (reconciledState.columnsOrder !== columnsOrder) {
      setColumnsOrder(reconciledState.columnsOrder);
    }
    if (reconciledState.columnsToggle !== columnsToggle) {
      setColumnsToggle(reconciledState.columnsToggle);
    }
  }, [
    columnsOrder,
    columnsToggle,
    setColumnsOrder,
    setColumnsToggle,
    reconciledState.columnsOrder,
    reconciledState.columnsToggle,
    storageKey,
  ]);

  return {
    effectiveColumns,
    columnsOrder,
    setColumnsOrder,
    resetColumnsOrder: () => setColumnsOrder(defaults.columnsOrder),
    columnsToggle,
    setColumnsToggle,
    resetColumnsToggle: () => setColumnsToggle(defaults.columnsToggle),
    columnsWidth,
    setColumnsWidth,
    setColumnWidth: (accessor, width) =>
      setColumnsWidth(previous => previous.map(column => (Object.hasOwn(column, accessor) ? { [accessor]: width } : column))),
    resetColumnsWidth: () => setColumnsWidth(defaults.columnsWidth),
  };
}

export const EditRowColumn = <T extends Record<string, unknown>>({
  onRowEdit,
  isRowEditable,
}: {
  onRowEdit: (record: T) => void;
  isRowEditable?: (record: T) => boolean;
}): DatatableColumn<T> => ({
  title: "",
  accessor: "",
  width: 40,
  textAlignment: "center",
  render: rowData => {
    if (isRowEditable?.(rowData) === false) {
      return null;
    }
    return (
      <ActionIcon variant="transparent" onClick={() => onRowEdit(rowData)} className={css.editRowButton}>
        <IconPencil stroke={1.5} size={16} />
      </ActionIcon>
    );
  },
});

/** The old createStyles shape remains an owned, static class map for existing callers. */
export const useDataTablePaginationStyles = () => ({ classes: { pagination: css.pagination } });

interface DatatablePagination {
  currentPage: number;
  perPage: number;
  total: number;
  onChange: (page: number) => void;
}

interface DatatableSort {
  apply: (status: DataTableSortStatus) => void;
  dir: DataTableSortDirection;
  field: string;
}

/**
 * Row selection on the opinionated table: a checkbox column plus the vendor's header select-all over the
 * current page. Selection spans pages: records selected on another page stay in `selectedRecords`, matched
 * by `id`. Omitted renders no checkbox column.
 */
interface DatatableSelection<T> {
  selectedRecords: T[];
  onSelectedRecordsChange: (records: T[]) => void;
  /** A record this returns false for renders a disabled checkbox and is skipped by the header select-all. */
  isRecordSelectable?: (record: T) => boolean;
  /** Why a record cannot be selected, shown when hovering its disabled checkbox; null for a selectable record. */
  unselectableReason?: (record: T) => string | null;
  /** Accessible text for a selectable row checkbox. */
  recordLabel?: (record: T, index: number) => string;
  /** Accessible text for the current-page select-all checkbox. */
  headerLabel?: string;
}

interface DatatableBaseProps<T> {
  isLoading?: boolean;
  height?: number | string;
  minHeight?: number;
  noRecordsIcon?: TablerIcon;
  noRecordsText?: string;
  pagination?: DatatablePagination;
  records: T[];
  sort?: DatatableSort;
  tableKey: string;
  onRowClick?: (record: T) => void;
  rowStyle?: (record: T, index: number) => React.CSSProperties;
  headerVerticalAlign?: React.CSSProperties["verticalAlign"];
  className?: string;
  classNames?: Record<string, string | undefined>;
  /** Keeps the final column reachable while its row is hovered. */
  hoverStickyLastColumn?: boolean;
  selection?: DatatableSelection<T>;
}

interface DatatableStandardProps<T> extends DatatableBaseProps<T> {
  columns: DatatableColumn<T>[];
  columnsManaged?: false | undefined;
}

interface DatatableManagedProps<T> extends DatatableBaseProps<T> {
  columns: DataTableColumn<T>[];
  columnsManaged: true;
  setColumnWidth: DataTableColumnsState<T>["setColumnWidth"];
}

export type DatatableProps<T> = DatatableStandardProps<T> | DatatableManagedProps<T>;

/**
 * Replaces the vendor's default row checkbox props, so its default aria-label is kept for a selectable record.
 * An unselectable record's reason lives on a focusable checkbox wrapper because disabled inputs do not
 * reliably receive pointer or keyboard events. The wrapper exposes the reason to assistive technology
 * and renders the same tooltip on hover or keyboard focus.
 */
const recordSelectionCheckboxProps =
  <T,>(selection: DatatableSelection<T>) =>
  (record: T, index: number) => {
    const reason = selection.unselectableReason?.(record) ?? null;
    const label = selection.recordLabel?.(record, index) ?? `Select record ${index + 1}`;
    if (reason === null) {
      return { "aria-label": label };
    }
    const explanation = `${label.replace(/^Select\b/, "Cannot select")}: ${reason}`;
    return {
      "aria-label": explanation,
      wrapperProps: {
        title: reason,
        tabIndex: 0,
        role: "note",
        "aria-label": reason,
        "data-selection-reason": reason,
        className: css.unselectableSelectionReason,
      },
    };
  };

const interactionProps = <T,>(
  props: DatatableProps<T>,
): Pick<
  DataTableProps<T>,
  | "highlightOnHover"
  | "onRowClick"
  | "sortStatus"
  | "onSortStatusChange"
  | "selectedRecords"
  | "onSelectedRecordsChange"
  | "isRecordSelectable"
  | "getRecordSelectionCheckboxProps"
  | "selectionHeaderLabel"
  | "hoverStickyLastColumn"
> => ({
  ...(props.hoverStickyLastColumn ? { hoverStickyLastColumn: true } : {}),
  ...(props.onRowClick ? { highlightOnHover: true, onRowClick: (record: T) => props.onRowClick?.(record) } : {}),
  ...(props.sort
    ? {
        sortStatus: { columnAccessor: props.sort.field, direction: props.sort.dir },
        onSortStatusChange: props.sort.apply,
      }
    : {}),
  ...(props.selection
    ? {
        selectedRecords: props.selection.selectedRecords,
        onSelectedRecordsChange: props.selection.onSelectedRecordsChange,
        selectionHeaderLabel: props.selection.headerLabel,
        ...(props.selection.isRecordSelectable ? { isRecordSelectable: props.selection.isRecordSelectable } : {}),
        ...(props.selection.unselectableReason || props.selection.recordLabel
          ? { getRecordSelectionCheckboxProps: recordSelectionCheckboxProps(props.selection) }
          : {}),
      }
    : {}),
});

const paginationProps = (pagination: DatatablePagination | undefined, classNames: Record<string, string | undefined>) =>
  pagination
    ? {
        classNames,
        page: pagination.currentPage,
        totalRecords: pagination.total,
        recordsPerPage: pagination.perPage,
        paginationText: ({ from, to, totalRecords }: { from: number; to: number; totalRecords: number }) =>
          `${from} - ${to} of ${totalRecords}`,
        onPageChange: pagination.onChange,
      }
    : {};

const noRecordsTextFor = <T,>(props: DatatableBaseProps<T>) => (props.records.length === 0 && !props.isLoading ? props.noRecordsText : "");

type HeaderVerticalAlignStyle = React.CSSProperties & {
  "--shelfcycle-table-header-vertical-align"?: React.CSSProperties["verticalAlign"];
};

const headerStyle = (verticalAlign: React.CSSProperties["verticalAlign"] | undefined): HeaderVerticalAlignStyle | undefined =>
  verticalAlign === undefined ? undefined : { "--shelfcycle-table-header-vertical-align": verticalAlign };

/**
 * Mantine 6's column drop (mantine-datatable 6.0.8 swapColumns): the dragged accessor leaves the stored order
 * and is inserted at the index the drop target held, so an accessor with no current definition keeps its place.
 */
const moveColumn = (order: string[], source: string, target: string): string[] => {
  const sourceIndex = order.indexOf(source);
  const targetIndex = order.indexOf(target);
  return sourceIndex === -1 || targetIndex === -1 ? order : arrayMove(order, sourceIndex, targetIndex);
};

interface DraggableColumnTitleProps {
  accessor: string;
  title: React.ReactNode;
  onDragStart: (accessor: string) => void;
  onDragEnd: () => void;
  onDrop: (accessor: string) => void;
}

/**
 * Mantine 6's header drag affordance (mantine-datatable 6.0.8 DataTableHeaderCell): a grip, a grab cursor, a
 * hover and drag-over fill, and HTML5 drag events on the title. Datatable renders it inside the column title
 * because mantine-datatable 7's own drag needs a storage key that its column hook prunes (SC-2703).
 */
function DraggableColumnTitle({ accessor, title, onDragStart, onDragEnd, onDrop }: DraggableColumnTitleProps) {
  const [dragOver, setDragOver] = React.useState(false);
  const text = title ?? humanize(accessor);

  return (
    <div
      className={joinClassNames(css.draggableColumnTitle, dragOver ? css.draggableColumnTitleOver : undefined)}
      draggable
      onDragStart={event => {
        event.stopPropagation();
        onDragStart(accessor);
        setDragOver(false);
      }}
      onDragEnter={() => setDragOver(true)}
      onDragOver={event => {
        event.preventDefault();
        setDragOver(true);
      }}
      onDragLeave={() => setDragOver(false)}
      onDrop={() => {
        setDragOver(false);
        onDrop(accessor);
      }}
      onDragEnd={onDragEnd}
    >
      <div className={css.draggableColumnGrip} role="img" aria-label="Drag column">
        <ActionIcon variant="subtle" size="xs" className={css.draggableColumnGripButton} onClick={event => event.stopPropagation()}>
          <IconGripVertical />
        </ActionIcon>
      </div>
      <div className={css.draggableColumnText} title={typeof text === "string" ? text : undefined}>
        {text}
      </div>
    </div>
  );
}

interface ResizableColumnTitleProps {
  accessor: string;
  title: React.ReactNode;
  setColumnWidth: DataTableColumnsState<unknown>["setColumnWidth"];
}

/** Mantine 6's header resize interaction, writing through the same owned state as column order and visibility. */
function ResizableColumnTitle({ accessor, title, setColumnWidth }: ResizableColumnTitleProps) {
  const cleanupRef = React.useRef<(() => void) | undefined>(undefined);

  React.useEffect(() => () => cleanupRef.current?.(), []);

  return (
    <>
      <span className={css.resizableColumnText}>{title ?? humanize(accessor)}</span>
      <div
        aria-label={`Resize ${humanize(accessor)} column`}
        aria-orientation="vertical"
        className={css.resizableColumnHandle}
        draggable={false}
        role="separator"
        onClick={event => event.stopPropagation()}
        onDragStart={event => {
          event.preventDefault();
          event.stopPropagation();
        }}
        onMouseDown={event => {
          event.preventDefault();
          event.stopPropagation();
          cleanupRef.current?.();

          const headerCell = event.currentTarget.closest("th");
          if (headerCell === null) {
            return;
          }

          const startX = event.clientX;
          const startWidth = headerCell.getBoundingClientRect().width;
          const resize = (moveEvent: MouseEvent) => {
            setColumnWidth(accessor, `${Math.max(1, startWidth + moveEvent.clientX - startX)}px`);
          };
          const cleanup = () => {
            document.removeEventListener("mousemove", resize);
            document.removeEventListener("mouseup", finish);
            document.body.style.cursor = "";
            cleanupRef.current = undefined;
          };
          const finish = (upEvent: MouseEvent) => {
            resize(upEvent);
            cleanup();
          };

          cleanupRef.current = cleanup;
          document.addEventListener("mousemove", resize);
          document.addEventListener("mouseup", finish);
          document.body.style.cursor = "col-resize";
        }}
        onDoubleClick={event => {
          event.preventDefault();
          event.stopPropagation();
          setColumnWidth(accessor, "initial");
        }}
      />
    </>
  );
}

const withOwnedResize = <T,>(
  columns: DataTableColumn<T>[],
  setColumnWidth: DataTableColumnsState<T>["setColumnWidth"],
): DataTableColumn<T>[] =>
  columns.map(({ resizable, ...column }) =>
    resizable
      ? {
          ...column,
          titleClassName: joinClassNames(column.titleClassName, css.resizableColumnHeader),
          title: <ResizableColumnTitle accessor={column.accessor} title={column.title} setColumnWidth={setColumnWidth} />,
        }
      : column,
  );

function DatatableWithInternalColumns<T>(props: DatatableStandardProps<T>) {
  const { classes: paginationClasses } = useDataTablePaginationStyles();
  const classNames = props.classNames ? { ...paginationClasses, ...props.classNames } : paginationClasses;
  const columns = props.columns.map(({ alignment, textAlignment, ...column }) => ({
    ...column,
    textAlignment: alignment ?? textAlignment,
  }));
  const { effectiveColumns, setColumnsOrder, setColumnWidth } = useDatatableColumns<T>({ key: props.tableKey, columns });
  const draggedAccessor = React.useRef<string | undefined>(undefined);

  // Mantine 6 also handed tableKey to the vendor, whose header drag reordered the stored column order.
  // mantine-datatable 7's column hook prunes that storage on every render, so the vendor gets no key and no
  // draggable column: the header drag is owned here and writes through useDatatableColumns, the only writer.
  const dropColumn = (target: string) => {
    const source = draggedAccessor.current;
    draggedAccessor.current = undefined;
    if (source !== undefined && source !== target) {
      setColumnsOrder(order => moveColumn(order, source, target));
    }
  };
  const headerColumns = withOwnedResize(
    effectiveColumns.map(({ draggable, ...column }) =>
      draggable && props.tableKey
        ? {
            ...column,
            title: (
              <DraggableColumnTitle
                accessor={column.accessor}
                title={column.title}
                onDragStart={accessor => {
                  draggedAccessor.current = accessor;
                }}
                onDragEnd={() => {
                  draggedAccessor.current = undefined;
                }}
                onDrop={dropColumn}
              />
            ),
          }
        : column,
    ),
    setColumnWidth,
  );

  return (
    <DataTable
      {...interactionProps(props)}
      {...paginationProps(props.pagination, classNames)}
      className={joinClassNames(css.datatable, props.className)}
      classNames={classNames}
      columns={headerColumns}
      fetching={props.isLoading}
      height={props.height}
      minHeight={props.minHeight ?? 150}
      noRecordsIcon={props.noRecordsIcon ? <props.noRecordsIcon /> : undefined}
      noRecordsText={noRecordsTextFor(props)}
      records={props.records}
      rowStyle={props.rowStyle}
      style={headerStyle(props.headerVerticalAlign)}
    />
  );
}

/**
 * The opinionated table: pass tableKey when columns should persist and reconcile managed state.
 * Use DataTable for an unopinionated table that does not need this application policy.
 */
export function Datatable<T>(props: DatatableProps<T>) {
  if (!props.columnsManaged) {
    return <DatatableWithInternalColumns {...props} />;
  }

  return <DatatableWithManagedColumns {...props} />;
}

function DatatableWithManagedColumns<T>(props: DatatableManagedProps<T>) {
  const { classes: paginationClasses } = useDataTablePaginationStyles();
  const classNames = props.classNames ? { ...paginationClasses, ...props.classNames } : paginationClasses;
  const columns = withOwnedResize(
    props.columns.map(({ toggleable: _toggleable, draggable: _draggable, defaultToggle: _defaultToggle, ...column }) => column),
    props.setColumnWidth,
  );

  return (
    <DataTable
      {...interactionProps(props)}
      {...paginationProps(props.pagination, classNames)}
      className={joinClassNames(css.datatable, props.className)}
      classNames={classNames}
      columns={columns}
      fetching={props.isLoading}
      height={props.height}
      minHeight={props.minHeight ?? 150}
      noRecordsIcon={props.noRecordsIcon ? <props.noRecordsIcon /> : undefined}
      noRecordsText={noRecordsTextFor(props)}
      records={props.records}
      rowStyle={props.rowStyle}
      storeColumnsKey={undefined}
      style={headerStyle(props.headerVerticalAlign)}
    />
  );
}

/**
 * Generic facade for callers that need the vendor's table behavior without Datatable's tableKey policy.
 * It absorbs mantine-datatable 7's renames (SC-2703): the owned props, callback payloads (record, then
 * recordIndex) and Mantine 6's row-separator and header-colour defaults are unchanged. mantine-datatable 7
 * removed the built-in row context menu, so the untyped rowContextMenu prop (no call site passes it) is
 * accepted and not forwarded.
 */
export function DataTable<T>(props: DataTableProps<T>) {
  const {
    columns,
    defaultColumnProps,
    hoverStickyLastColumn,
    withBorder,
    verticalAlignment,
    fontSize,
    rowContextMenu: _rowContextMenu,
    onCellClick,
    onRowClick,
    onSortStatusChange,
    rowExpansion,
    rowStyle,
    selectionHeaderLabel,
    selectionHeaderDisabled,
    className,
    classNames,
    rowBorderColor,
    ...tableProps
  } = props;
  const columnStateStored = Boolean(tableProps.storeColumnsKey);

  // The caller's record type is handed over explicitly: inferred through the generic rest and the callbacks below,
  // it resolves to unknown, which would erase T from every column, callback and row-expansion payload.
  return (
    <DataTableRoot<T>
      {...tableProps}
      className={joinClassNames(hoverStickyLastColumn ? css.hoverStickyLastColumn : undefined, className)}
      classNames={{
        ...classNames,
        header: joinClassNames(css.headerBelowToolbar, classNames?.header),
        table: joinClassNames(css.dataTableTable, classNames?.table),
      }}
      columns={columns.map(column => ({ ...toVendorColumnFields<T>(column, columnStateStored), accessor: column.accessor }))}
      defaultColumnProps={defaultColumnProps && toVendorColumnFields<T>(defaultColumnProps, columnStateStored)}
      rowBorderColor={rowBorderColor ?? MANTINE_6_ROW_BORDER_COLOR}
      withTableBorder={withBorder}
      // Mantine 6 drew the last row's bottom border whenever the table was shorter than its viewport;
      // mantine-datatable 7 does only when withRowBorders is passed explicitly.
      withRowBorders
      verticalAlign={verticalAlignment}
      fz={fontSize}
      onCellClick={toVendorCellClick<T>(columns, onCellClick)}
      onRowClick={onRowClick && (({ event, record, index }) => onRowClick(record, index, event))}
      // mantine-datatable 7 adds sortKey to the emitted status; the owned status carries only these two keys.
      onSortStatusChange={onSortStatusChange && (({ columnAccessor, direction }) => onSortStatusChange({ columnAccessor, direction }))}
      rowExpansion={rowExpansion && toVendorRowExpansion<T>(rowExpansion)}
      rowStyle={toVendorStyle<T>(rowStyle)}
      allRecordsSelectionCheckboxProps={{
        ...ALL_RECORDS_SELECTION_CHECKBOX,
        "aria-label": selectionHeaderLabel ?? ALL_RECORDS_SELECTION_CHECKBOX["aria-label"],
        ...(selectionHeaderDisabled === undefined ? {} : { disabled: selectionHeaderDisabled }),
      }}
      data-sc-print-wide="true"
    />
  );
}

type TableVariableStyle = React.CSSProperties & {
  "--shelfcycle-table-font-size": string;
  "--shelfcycle-table-horizontal-spacing": string;
  "--shelfcycle-table-vertical-spacing": string;
};

type SizeScale = Readonly<Record<string, string>> & { readonly md: string };

/** Mantine 6's getSize: a theme key reads its token, a number converts to rem, any other string is used verbatim. */
function mantine6Size(size: string | number, sizes: SizeScale): string {
  if (typeof size === "number") {
    return rem(size);
  }
  return sizes[size] ?? (size || sizes.md);
}

/**
 * Mantine 7's Table styles only its Table.Th/Td/Tr parts, and the owned Table takes plain children, so
 * Table applies Mantine 6's rules itself (tables.module.css) with Mantine 6's defaults and DOM markers:
 * data-striped and data-hover on the table, cell font size and padding resolved as Mantine 6 did (SC-2703).
 * Mantine 7 renamed withBorder to withTableBorder; the owned name is unchanged.
 */
export function Table({
  striped,
  highlightOnHover,
  withBorder,
  withColumnBorders,
  horizontalSpacing = "xs",
  verticalSpacing = 7,
  fontSize = "sm",
  className,
  style,
  ...props
}: TableProps) {
  const tableStyle: TableVariableStyle = {
    "--shelfcycle-table-font-size": mantine6Size(fontSize, tokens.typography.fontSize),
    "--shelfcycle-table-horizontal-spacing": mantine6Size(horizontalSpacing, tokens.spacing),
    "--shelfcycle-table-vertical-spacing": mantine6Size(verticalSpacing, tokens.spacing),
    ...style,
  };

  return (
    <TableRoot
      {...props}
      className={joinClassNames(css.table, className)}
      style={tableStyle}
      withTableBorder={withBorder}
      data-striped={striped || undefined}
      data-hover={highlightOnHover || undefined}
      data-with-column-borders={withColumnBorders || undefined}
      data-sc-print-wide="true"
    />
  );
}

export interface ColumnToggleMenuProps {
  columnsToggle: DataTableColumnToggle[];
  setColumnsToggle: React.Dispatch<React.SetStateAction<DataTableColumnToggle[]>>;
  resetColumnsToggle: () => void;
  columnsOrder: string[];
  setColumnsOrder: React.Dispatch<React.SetStateAction<string[]>>;
  resetColumnsOrder: () => void;
  labels: Record<string, string>;
}

interface SortableColumnItemProps {
  column: DataTableColumnToggle;
  label: string;
  onToggle: (accessor: string, checked: boolean) => void;
}

const SortableColumnItem = ({ column, label, onToggle }: SortableColumnItemProps) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: column.accessor });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    zIndex: isDragging ? 10 : undefined,
    opacity: isDragging ? 0.8 : 1,
  };

  return (
    <Flex ref={setNodeRef} className={css.sortableColumn} style={style} align="center" gap="xs" px="sm" py={6}>
      <Flex {...attributes} {...listeners} className={css.dragHandle} align="center">
        <IconGripVertical size={14} className={css.dragHandleIcon} />
      </Flex>
      {column.toggleable ? (
        <Checkbox
          checked={column.toggled}
          label={label}
          size="sm"
          style={{ flex: 1 }}
          onChange={event => onToggle(column.accessor, event.currentTarget.checked)}
        />
      ) : (
        <Flex align="center" gap="xs" style={{ flex: 1 }}>
          <IconLock size={14} className={css.lockIcon} />
          <Text fz="sm">{label}</Text>
        </Flex>
      )}
    </Flex>
  );
};

export const ColumnToggleMenu = ({
  columnsToggle,
  setColumnsToggle,
  resetColumnsToggle,
  columnsOrder,
  setColumnsOrder,
  resetColumnsOrder,
  labels,
}: ColumnToggleMenuProps) => {
  const [opened, { toggle, close }] = useDisclosure(false);
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );
  const toggleByAccessor = React.useMemo(() => new Map(columnsToggle.map(column => [column.accessor, column])), [columnsToggle]);
  const orderedItems = React.useMemo(
    () =>
      columnsOrder
        .filter(accessor => Object.hasOwn(labels, accessor))
        .map(accessor => toggleByAccessor.get(accessor))
        .filter((column): column is DataTableColumnToggle => column !== undefined),
    [columnsOrder, labels, toggleByAccessor],
  );
  const handleToggle = React.useCallback(
    (accessor: string, checked: boolean) =>
      setColumnsToggle(previous => previous.map(column => (column.accessor === accessor ? { ...column, toggled: checked } : column))),
    [setColumnsToggle],
  );
  const handleDragEnd = React.useCallback(
    ({ active, over }: DragEndEvent) => {
      if (!over || active.id === over.id) {
        return;
      }
      setColumnsOrder(previous => {
        const oldIndex = previous.indexOf(String(active.id));
        const newIndex = previous.indexOf(String(over.id));
        return oldIndex === -1 || newIndex === -1 ? previous : arrayMove(previous, oldIndex, newIndex);
      });
    },
    [setColumnsOrder],
  );
  const reset = React.useCallback(() => {
    resetColumnsOrder();
    resetColumnsToggle();
  }, [resetColumnsOrder, resetColumnsToggle]);

  return (
    <Popover opened={opened} onClose={close} position="bottom-end" shadow="md" width={260} withinPortal>
      <Popover.Target>
        <Tooltip label="Manage Columns" position="bottom" withArrow>
          <ActionIcon variant="default" size="lg" onClick={toggle} aria-label="Manage columns">
            <IconColumns size={16} />
          </ActionIcon>
        </Tooltip>
      </Popover.Target>
      <Popover.Dropdown p="xs">
        <Text fw={600} fz="sm" px="sm" py={4}>
          Columns
        </Text>
        <Divider my={4} />
        <ScrollArea.Autosize mah={360}>
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            modifiers={[restrictToVerticalAxis, restrictToParentElement]}
            onDragEnd={handleDragEnd}
          >
            <SortableContext items={orderedItems.map(column => column.accessor)} strategy={verticalListSortingStrategy}>
              {orderedItems.map(column => (
                <SortableColumnItem
                  key={column.accessor}
                  column={column}
                  label={labels[column.accessor] ?? column.accessor}
                  onToggle={handleToggle}
                />
              ))}
            </SortableContext>
          </DndContext>
        </ScrollArea.Autosize>
        <Divider my={4} />
        <UnstyledButton type="button" className={css.resetButton} onClick={reset}>
          <IconRefresh size={14} className={css.resetIcon} />
          <Text fz="sm">Reset Columns</Text>
        </UnstyledButton>
      </Popover.Dropdown>
    </Popover>
  );
};
